/**
 * Product schema for the duplicate-group comparison engine.
 *
 * Describes how a duplicate-PRODUCT group member renders: identity header
 * (SKU linking to Lightspeed) and the attribute rows a reviewer needs to
 * judge near-duplicates (image, names, supplier, prices, stock, recency).
 * Structure and behaviour live in `GroupComparisonTable`; this file is
 * content only.
 */
import React from 'react'
import { Box, Link, Typography } from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'

import {
  formatCurrency,
  formatDate,
} from '../../../../../utils/inspectorFormatters'
import { handleLightspeedLinkClick } from '../../../../../utils/externalLinks'
import type { DuplicateGroupMember } from '../../../../../services/inspector/api'
import type { GroupComparisonSchema } from '../comparisonTypes'

const THUMB_SIZE = 56

const fmtStock = (n: number | null | undefined): string =>
  n == null
    ? '-'
    : Number(n).toLocaleString('en-AU', { maximumFractionDigits: 2 })

const boolStr = (v: boolean | null | undefined): string =>
  v == null ? '-' : v ? '✓' : '✗'

const thumb = (m: DuplicateGroupMember): React.ReactNode =>
  m.image_thumbnail_url ? (
    <Box
      component="img"
      src={m.image_thumbnail_url}
      alt={m.name}
      sx={{
        width: THUMB_SIZE,
        height: THUMB_SIZE,
        objectFit: 'cover',
        borderRadius: 1,
        border: '1px solid rgba(0,0,0,0.08)',
      }}
    />
  ) : (
    <Box
      sx={{
        width: THUMB_SIZE,
        height: THUMB_SIZE,
        borderRadius: 1,
        border: '1px dashed rgba(0,0,0,0.12)',
        // inline-flex so the placeholder centers in the cell like the
        // (inline) <img> it stands in for.
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'text.disabled',
      }}
    >
      <Typography variant="caption">No image</Typography>
    </Box>
  )

export const productComparisonSchema: GroupComparisonSchema<DuplicateGroupMember> =
  {
    domain: 'product_setup',
    exceptionCode: 'ex-product-ls-duplicate',
    exceptionTypeName: 'Duplicate product',
    cornerLabel: 'Product',
    minColumnWidth: 180,
    memberId: (m) => String(m.ls_id),
    memberTitle: (m) => {
      const sku = m.sku ? String(m.sku) : ''
      const name = m.name ? String(m.name) : ''
      if (sku && name) return `${sku} - ${name}`
      return sku || name || `Product ${String(m.ls_id).slice(0, 8)}`
    },
    memberHeader: (m) =>
      m.lightspeed_product_link ? (
        <Link
          href={m.lightspeed_product_link}
          onClick={(e) =>
            handleLightspeedLinkClick(
              e,
              m.lightspeed_product_link!,
              `Product ${m.sku || m.name}`,
            )
          }
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.25,
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
            fontWeight: 600,
          }}
        >
          {m.sku || m.ls_id.slice(0, 8)}
          <OpenInNewIcon sx={{ fontSize: 13 }} />
        </Link>
      ) : (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {m.sku || m.ls_id.slice(0, 8)}
        </Typography>
      ),
    attrs: [
      {
        key: 'image',
        label: 'Image',
        getValue: (m) => m.image_thumbnail_url || '',
        render: thumb,
        neverDiff: true,
      },
      { key: 'sku', label: 'SKU', getValue: (m) => m.sku || '' },
      { key: 'name', label: 'Full name', getValue: (m) => m.name || '' },
      {
        key: 'variant_name',
        label: 'Variant name',
        getValue: (m) => m.variant_name || '',
      },
      {
        key: 'supplier',
        label: 'Supplier',
        getValue: (m) => m.supplier_name || '',
      },
      {
        key: 'supplier_code',
        label: 'Supplier code',
        getValue: (m) => m.supplier_code || '',
      },
      { key: 'brand', label: 'Brand ID', getValue: (m) => m.brand_id || '' },
      {
        // Leaf category (not root) is deliberate here: this view compares
        // suspected duplicates side-by-side, and the leaf is the granular
        // field that actually differs between near-duplicates (e.g.
        // "Pumps/1HP" vs "Pumps/1.5HP"). List-view tables use the root
        // ("Main Category") via formatMainCategory() - don't swap this one.
        key: 'category',
        label: 'Category',
        getValue: (m) => m.category_name || '',
      },
      {
        key: 'price_inc_tax',
        label: 'Price (inc tax)',
        getValue: (m) => m.price_including_tax ?? null,
        render: (m) =>
          m.price_including_tax != null
            ? formatCurrency(m.price_including_tax)
            : '-',
        numeric: true,
      },
      {
        key: 'supply_price',
        label: 'Supply price',
        getValue: (m) => m.supply_price ?? null,
        render: (m) =>
          m.supply_price != null ? formatCurrency(m.supply_price) : '-',
        numeric: true,
      },
      {
        key: 'total_stock',
        label: 'Total stock',
        getValue: (m) => m.total_stock ?? 0,
        render: (m) => fmtStock(m.total_stock),
        numeric: true,
      },
      {
        key: 'last_sold',
        label: 'Last sold',
        getValue: (m) => m.last_sale_date_local || '',
        render: (m) =>
          m.last_sale_date_local ? formatDate(m.last_sale_date_local) : '-',
        numeric: true,
      },
      {
        key: 'created_at',
        label: 'Created (LS)',
        getValue: (m) => (m.created_at ? String(m.created_at).slice(0, 10) : ''),
        render: (m) => (m.created_at ? formatDate(m.created_at) : '-'),
        numeric: true,
      },
      {
        key: 'active',
        label: 'Active',
        getValue: (m) => !!m.active,
        render: (m) => boolStr(m.active),
      },
      {
        key: 'has_inventory',
        label: 'Has inventory',
        getValue: (m) => !!m.has_inventory,
        render: (m) => boolStr(m.has_inventory),
      },
      {
        key: 'score',
        label: 'Match score',
        getValue: (m) => m.score ?? 0,
        render: (m) => (m.score != null ? `${m.score}%` : '-'),
        neverDiff: true,
        numeric: true,
      },
    ],
  }
